import { BracketSeries, GameId } from "@portal-frontend-ssr/blast-api";
import { AdaptiveLink } from "@portal-frontend-ssr/ui";
import classNames from "classnames";
import { useMemo } from "react";
import { Handle, Position } from "reactflow";
import { TeamSlot } from "~/routes/_cs.cs.bounty-simulator._index/components/TeamDropdown";
import { generateMatchPageUrl } from "~/shared/helpers/url/generateMatchPageUrl";
import { generateTeamLogoSrc } from "~/shared/helpers/url/generateTeamLogoSrc";

type FlowBracketMatchProps = {
  match: BracketSeries;
  tournamentName: string;
  gameId: GameId;
  selectedTeam?: string;
  setSelectedTeam?: (team: string) => void;
  height?: number;
  width?: number;
  onTeamClick?: (match: BracketSeries, team: TeamSlot | null) => void;
  pickedResults?: Record<string, TeamSlot>;
};

type TeamLinkProps = {
  originalTeam?: BracketSeries["teamA"] | BracketSeries["teamB"];
  simulatedTeam?: BracketSeries["teamASimulated"] | BracketSeries["teamBSimulated"];
  score?: number;
  showScore: boolean;
  isSelected: boolean;
  isClickable: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
  matchPageUrl?: string;
  onTeamClick?: (match: BracketSeries, team: TeamSlot | null) => void;
  match: BracketSeries;
  pickedResults?: Record<string, TeamSlot>;
  teamSlot: TeamSlot;
};

const TeamLink = ({
  originalTeam,
  simulatedTeam,
  score,
  showScore,
  isSelected,
  isClickable,
  onMouseEnter,
  onMouseLeave,
  onClick,
  matchPageUrl,
  onTeamClick,
  match,
  pickedResults,
  teamSlot,
}: TeamLinkProps) => {
  const team = simulatedTeam ?? originalTeam;
  const state: "picked" | "not-picked" | "picked-incorrect" | "not-picked-was-correct" = useMemo(() => {
    if (!pickedResults?.[match.uuid]) return "not-picked";
    const isThisPicked = pickedResults[match.uuid] === teamSlot;
    if (simulatedTeam && originalTeam?.uuid && simulatedTeam?.uuid !== originalTeam?.uuid) {
      if (isThisPicked) return "picked-incorrect";
      return "not-picked";
    }
    if (match.isCompleted) {
      const winnerTeam = match.teamAScore > match.teamBScore ? TeamSlot.A : TeamSlot.B;
      if (isThisPicked) {
        if (teamSlot === winnerTeam) return "picked";
        return "picked-incorrect";
      } else {
        if (teamSlot === winnerTeam) return "not-picked-was-correct";
        return "not-picked";
      }
    }
    if (isThisPicked) return "picked";

    return "not-picked";
  }, [
    match.isCompleted,
    match.teamAScore,
    match.teamBScore,
    match.uuid,
    originalTeam?.uuid,
    pickedResults,
    simulatedTeam,
    teamSlot,
  ]);
  const Content = useMemo(() => {
    return (
      <>
        {team?.uuid ? (
          <div
            className={classNames("flex w-full flex-row items-center gap-x-2", {
              "w-full justify-center": team?.shorthand === undefined,
            })}
          >
            {team?.uuid && (
              <img
                alt={team.shorthand}
                className="size-4 object-contain"
                src={generateTeamLogoSrc({ id: team.uuid, width: 24 })}
              />
            )}
            <span
              className={classNames("uppercase font-style-body-b4", {
                "h-4": team?.shorthand === undefined,
              })}
            >
              {team?.shorthand ?? "???"}
            </span>
          </div>
        ) : (
          <div className="flex w-full items-center justify-start gap-2 px-1">
            <div className="flex size-4 items-center justify-center rounded-full bg-background-80">
              <span className="text-white/90 font-style-label-l4">?</span>
            </div>
            <span className="text-center uppercase text-white font-style-body-b4">TBD</span>
          </div>
        )}
        {team?.shorthand && <span className="font-style-label-l4">{showScore ? score : "-"}</span>}
      </>
    );
  }, [team, score, showScore]);

  if (onTeamClick && team?.uuid) {
    return (
      <div className="flex h-8 w-full rounded-small bg-background-100">
        <button
          className={classNames(
            "flex w-full items-center justify-between rounded-small border border-background-100 bg-background-100 px-2 py-1",
            {
              "border-success bg-success/20 bg-gradient-to-r from-transparent": state === "picked",
              "border-transparent bg-success/20": state === "not-picked-was-correct",
              "border-transparent": state === "not-picked",
              "border-error bg-error/20": state === "picked-incorrect",
            },
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={() => onTeamClick?.(match, teamSlot)}
        >
          {Content}
        </button>
      </div>
    );
  }

  if (matchPageUrl) {
    return (
      <AdaptiveLink
        className={classNames(
          "flex h-8 flex-row items-center justify-between rounded-small border border-background-100 bg-background-100 p-1",
          {
            "border-primary-100!": team?.uuid ? isSelected : false,
            "opacity-75": team?.isEliminated,
            "hover:cursor-pointer": isClickable,
          },
        )}
        to={matchPageUrl ?? ""}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {Content}
      </AdaptiveLink>
    );
  }

  return (
    <div className="flex h-8 cursor-default flex-row items-center justify-between rounded-small border border-background-100 bg-background-100 p-1">
      {Content}
    </div>
  );
};

export const FlowBracketMatch = ({
  match,
  height,
  width,
  selectedTeam,
  setSelectedTeam,
  tournamentName,
  gameId,
  onTeamClick,
  pickedResults,
}: FlowBracketMatchProps) => {
  const matchPageUrl = generateMatchPageUrl({
    game: "cs",
    matchUuid: match.uuid,
    tournamentName,
    teamAName: match.teamA?.shorthand,
    teamBName: match.teamB?.shorthand,
  });
  const showScore = match.isLive || match.isCompleted || match.teamAScore > 0 || match.teamBScore > 0;

  return (
    <div
      style={{
        height: height ?? 140,
        width: width ?? 250,
      }}
    >
      <span className="text-nowrap text-neutral font-style-label-l4">{match.name}</span>
      <div className="relative">
        <Handle type="target" position={Position.Left} className="invisible absolute top-1/2 h-2 w-0" />
        <div className="flex flex-col gap-y-1.5 rounded-small bg-background-90 p-1.5">
          <TeamLink
            originalTeam={match.teamA}
            simulatedTeam={match.teamASimulated}
            score={match.teamAScore}
            showScore={showScore}
            isSelected={selectedTeam === match.teamA?.uuid}
            isClickable={gameId === "cs"}
            matchPageUrl={gameId === "cs" ? matchPageUrl : undefined}
            onMouseEnter={() => (!onTeamClick ? setSelectedTeam?.(match.teamA?.uuid ?? "") : undefined)}
            onMouseLeave={() => (!onTeamClick ? setSelectedTeam?.("") : undefined)}
            onTeamClick={onTeamClick}
            match={match}
            pickedResults={pickedResults}
            teamSlot={TeamSlot.A}
          />
          <TeamLink
            originalTeam={match.teamB}
            simulatedTeam={match.teamBSimulated}
            score={match.teamBScore}
            showScore={showScore}
            isSelected={selectedTeam === match.teamB?.uuid}
            isClickable={gameId === "cs"}
            matchPageUrl={gameId === "cs" ? matchPageUrl : undefined}
            onMouseEnter={() => (!onTeamClick ? setSelectedTeam?.(match.teamB?.uuid ?? "") : undefined)}
            onMouseLeave={() => (!onTeamClick ? setSelectedTeam?.("") : undefined)}
            onTeamClick={onTeamClick}
            match={match}
            pickedResults={pickedResults}
            teamSlot={TeamSlot.B}
          />
        </div>
        <Handle type="source" position={Position.Right} className="invisible absolute top-1/2 h-2" />
      </div>
    </div>
  );
};
